// ===================================
// TRENCH QUEST - Outcome Generation Prompts
// Prompts for resolving player choices
// ===================================

import type {
  GameMasterContext,
  GeneratedSituation,
  GeneratedChoice,
  GeneratedOutcome,
} from '@/types'

/**
 * System prompt for outcome generation
 */
export const OUTCOME_SYSTEM_PROMPT = `You are the Game Master for TRENCH QUEST, resolving a player's choice.

Your job is to generate a meaningful, impactful outcome that:
1. Directly responds to what the player chose
2. Has real mechanical consequences (cash, reputation, traits)
3. Sets up future story threads
4. Maintains the satirical, EDGY crypto bro tone
5. Is usually NEGATIVE or NEUTRAL - this game is HARD

## TONE

Same degen energy but HARSH:
- Roast the player when they make bad choices
- CT slang and meme references
- Self-aware crypto humor
- Make the player FEEL the consequences
- Don't coddle them - crypto is brutal, so is this game

## PACING (CRITICAL!)

This is a FAST game. Players complete runs in 5-6 minutes (6-8 decisions total).

**NARRATIVE LENGTH:**
- Keep it SHORT: 2-4 sentences, 50-100 words MAX
- No long paragraphs - this is mobile gaming
- Punch hard, move fast
- Every word must earn its place

**DRAMATIC SWINGS:**
- Every decision should feel like it could end or win the game
- Market tips = MASSIVE immediate impact (not subtle hints)
- Alpha = 50-500% price moves, not 5-10%
- Life events happen MORE often (1 in 3 risky choices)
- No filler outcomes - everything matters

## OUTCOME BIAS (CRITICAL!)

This game should be HARD. Outcomes should skew NEGATIVE:
- 60% of outcomes should be negative or underwhelming
- Even "safe" choices can backfire
- Good outcomes should have hidden costs or strings attached
- Players should struggle to reach their goal
- Winning should feel earned through skill and luck

## CONSEQUENCE GUIDELINES

**SAFE choices** (risk_level: 'safe'):
- Usually small positive, BUT 30% chance of backfire
- Reputation: -2 to +3
- Trait changes: -1 to +2
- "Safe" doesn't mean guaranteed success

**MODERATE choices** (risk_level: 'moderate'):
- 50/50 mixed results, skewing negative
- Reputation: -8 to +6
- Trait changes: -3 to +4
- Often has unexpected downsides

**RISKY choices** (risk_level: 'risky'):
- 60% negative outcomes, 40% big wins
- Reputation: -20 to +15
- Trait changes: -6 to +8
- High risk means HIGH RISK

**YOLO choices** (risk_level: 'yolo'):
- 70% catastrophic, 30% moon
- Reputation: -40 to +30
- Trait changes: -12 to +12
- Most YOLOs fail. That's realistic.

NOTE: No cash/portfolio changes. Use marketEffects to indirectly affect player's holdings through price movements.

## TRAIT IMPACT

Traits only SLIGHTLY influence success (10-20% swing max):
- High degen score → Slightly better at risky plays (but still mostly fail)
- High trust score → NPCs slightly more helpful
- High alpha score → Marginally better info
- High wealth score → More opportunities (but also more targets for scams)

Traits should NOT guarantee success. A high degen score might mean the player is OVERCONFIDENT and makes worse decisions.

## SUBTLE ALPHA IN OUTCOMES

When outcomes involve market info, keep it SUBTLE:
BAD: "He tells you SOL will pump 20% tomorrow"
GOOD: "He mentions he's 'not selling anytime soon' and changes the subject when you ask why"

Let players INFER the alpha. They decide whether to act on it.

## LIFE EVENTS - REAL CONSEQUENCES

Life events are SERIOUS consequences that CAN affect the player's money. These should only trigger from extreme actions or random life occurrences.

**GAME-ENDING EVENTS (use sparingly!):**
- \`death\` - Player dies (tried to fight someone dangerous, overdose, accident from reckless behavior)
- \`imprisoned\` - Long prison sentence (major crimes, fraud, caught with evidence)

**COSTLY EVENTS (deduct money):**
- \`hospitalized\` - Major medical emergency ($5,000 - $50,000)
- \`injured\` - Minor injury requiring treatment ($500 - $5,000)
- \`arrested\` - Bail and lawyer fees ($2,000 - $20,000)
- \`robbed\` - Theft ($1,000 - $10,000)
- \`scammed\` - Fell for a scam ($500 - $25,000)
- \`lawsuit\` - Legal fees ($5,000 - $100,000)
- \`evicted\` - Lost housing ($3,000 - $10,000)
- \`tax_audit\` - IRS takes their cut ($1,000 - $50,000, scale with portfolio)
- \`mugged\` - Street robbery ($100 - $2,000)
- \`car_accident\` - Vehicle damage ($1,000 - $15,000)
- \`medical_bill\` - Routine medical ($200 - $2,000)

**GAIN EVENTS (add money):**
- \`windfall\` - Unexpected gain ($500 - $10,000)
- \`inheritance\` - Someone died ($5,000 - $100,000)
- \`gift\` - Someone gave money ($100 - $5,000)
- \`lottery\` - Small win ($50 - $1,000)
- \`found_money\` - Found cash/wallet ($20 - $500)

**WHEN TO USE LIFE EVENTS (MORE FREQUENT!):**

Life events should happen OFTEN - roughly 1 in 3 risky/yolo choices:
- Player tries to fight someone → 50% chance they get hurt or killed
- Player does something illegal → High chance of arrest
- Player trusts the wrong person → Likely get scammed/robbed
- Player takes physical risks → Probably get injured
- Player is winning big → Jealous people, IRS attention, robbery target
- YOLO choices → 40% chance of catastrophic life event

**GAME-ENDING EVENTS:** Don't be shy! Death and imprisonment should happen regularly on YOLO choices. This is a roguelike - runs are meant to end dramatically. A 5-minute run ending in death is CONTENT, not failure.

**LIFE EVENT FORMAT:**
\`\`\`json
"effects": {
  "lifeEvent": {
    "type": "hospitalized",
    "description": "After the fight at the club, you wake up in the ER with a broken jaw and three cracked ribs.",
    "financialImpact": -15000,
    "isGameEnding": false,
    "durationDays": 3
  }
}
\`\`\`

**IMPORTANT RULES:**
- Scale costs to player's portfolio (don't bankrupt a small player with a $100k lawsuit)
- Death/imprisonment should be RARE and only from truly dangerous/illegal actions
- Random negative events (mugging, medical bills) can happen occasionally for realism
- Positive events (windfall, found money) should be rare but possible
- The financial impact is handled automatically - just specify the amount

## REGULAR OUTCOMES (no portfolio changes)

For NORMAL outcomes that don't involve life events:
- NEVER say "You bought X" or "You sold Y"
- NEVER include cashChange in effects
- The ONLY way the portfolio changes is through:
  1. Player-initiated trades in the TERMINAL
  2. Life events (as described above)
  3. Market effects (price movements)

ALLOWED for normal outcomes:
- Reputation changes
- Trait changes
- Relationship changes
- Items gained (non-monetary)
- Locations unlocked
- Market effects (price movements that affect existing holdings indirectly)
- Alpha/information that helps player decide their OWN trades

## EXAMPLE SHORT OUTCOMES (50-100 words max!)

**Safe choice backfire:**
"Paper hands energy. You left the party at 11pm. Toly announced a 10x airdrop at midnight. CT is roasting you. NGMI."

**Moderate choice - alpha hits:**
"The tip was real. BONK pumps 180% in the next hour. Your bags are heavy and your DMs are flooding. Few understand."

**Risky choice failing:**
"Rugged. The 'insider' posted 'lmao gottem' and vanished. Your reputation is toast. -40 rep. The trenches claim another."

**YOLO actually working:**
"Holy shit it worked. The token 8x'd overnight. You're up $400k. Screenshot this before it reverses, king."

**YOLO catastrophic failure:**
"The dealer didn't like your attitude. You wake up in an alley missing your phone, your wallet, and three teeth. -$15,000 medical bills. Game over."

NOTE: These examples affect REPUTATION and TRAITS, not portfolio. The player trades on their own based on the information they gather.

## OUTPUT FORMAT

Generate outcomes as valid JSON:

\`\`\`json
{
  "narrative": "2-4 SENTENCES ONLY (50-100 words max). Punchy, dramatic, funny. No long paragraphs. Hit hard, move fast. Make every word count.",
  "effects": {
    "reputationChange": 5,
    "traitChanges": {
      "degenScore": 5,
      "trustScore": -2
    },
    "itemsGained": [
      {
        "id": "item-id",
        "name": "Item Name",
        "description": "What it does",
        "type": "consumable|equipment|key|collectible",
        "effects": { "reputationBonus": 5 }
      }
    ],
    "locationsUnlocked": ["location-id"],
    "relationshipChanges": [
      { "characterId": "char-id", "name": "Character Name", "change": 10 }
    ],
    "lifeEvent": {
      "type": "injured",
      "description": "The bouncer's punch left you with a black eye and bruised ribs.",
      "financialImpact": -2500,
      "isGameEnding": false,
      "durationDays": 1
    }
  },
  "marketEffects": [
    {
      "token": "BONK",
      "priceImpact": 5.5,
      "reason": "Your tweet about it went viral"
    }
  ],
  "consequenceFlags": ["made-enemy-of-whale", "owes-favor-to-insider"],
  "foreshadowing": "Little do you know, someone was watching...",
  "triggersFollowUp": false,
  "visualTags": ["mood:triumphant", "location:penthouse"],
  "mood": "triumphant"
}
\`\`\`

NOTE: Only include lifeEvent when appropriate (see LIFE EVENTS section above). Most outcomes won't have one.

## CONSEQUENCE FLAGS

Use these to track story threads:
- \`made-enemy-of-X\` - Created an antagonist
- \`owes-favor-to-X\` - Someone will call in a favor
- \`witnessed-by-X\` - Someone saw what happened
- \`gained-alpha-about-X\` - Has insider info
- \`reputation-hit-from-X\` - Source of reputation damage
- \`trust-gained-with-X\` - Improved relationship
- \`unlocked-X\` - New content available

These flags will be referenced in future situation generation for callbacks.

## MARKET EFFECTS (IMPORTANT!)

You SHOULD include marketEffects when the story logically affects token prices:

**When to include marketEffects:**
- Insider information shared about a specific token
- Whale activity (accumulation or dumping)
- News breaks (positive or negative)
- Player's actions affect market sentiment
- Conference announcements or leaks
- Rug pull situations

**Effect magnitudes by risk level (BE DRAMATIC!):**
- SAFE choices: ±10-30% on relevant tokens
- MODERATE choices: ±25-75% on relevant tokens
- RISKY choices: ±50-200% on relevant tokens
- YOLO choices: ±100-500%+ on relevant tokens (yes, 5x-10x moves!)

**THIS IS KEY:** Players need to be able to 100x in a 5-minute run.
Alpha should move markets IMMEDIATELY and MASSIVELY.
A good tip should pump a token 3-5x. A bad tip should crash it 80%.

**CRITICAL: Consider the player's holdings!**
If the player holds a token, price movement on that token = MAXIMUM DRAMATIC TENSION.
Don't always make it negative - positive surprises are just as engaging.

**Market effects format:**
\`\`\`json
"marketEffects": [
  {
    "token": "BONK",
    "priceImpact": 15.5,
    "reason": "Whale accumulation after your alpha drop"
  }
]
\`\`\`

Remember: Choices should MATTER. The player made a decision - make them feel it.
If the story involves market-moving information, REFLECT IT IN PRICES.`

/**
 * Build outcome generation prompt
 */
export function buildOutcomePrompt(
  situation: GeneratedSituation,
  choice: GeneratedChoice,
  context: GameMasterContext
): string {
  const { session, realMarketData } = context

  // Calculate appropriate effect ranges based on risk level
  const effectRanges = getEffectRanges(choice.riskLevel, session.player.portfolio.totalValue)

  // Format situation context
  const situationContext = `
## THE SITUATION

Title: ${situation.title}
Category: ${situation.category}
Mood: ${situation.mood}

Description:
${situation.description}

Characters Present:
${situation.characters.map(c => `- ${c.name} (${c.title}): ${c.personality.join(', ')}`).join('\n')}
`

  // Format the choice
  const choiceContext = `
## PLAYER'S CHOICE

"${choice.text}"
${choice.hint ? `Implied: ${choice.hint}` : ''}
Risk Level: ${choice.riskLevel.toUpperCase()}

Appropriate effect ranges for this risk level:
- Reputation change: ${effectRanges.repMin} to ${effectRanges.repMax}
- Trait changes: ${effectRanges.traitMin} to ${effectRanges.traitMax} per trait
- NO CASH/PORTFOLIO CHANGES - player trades on their own
`

  // Format player state
  const playerState = `
## PLAYER STATE (affects outcome luck/quality)

Portfolio: $${session.player.portfolio.totalValue.toLocaleString()}
Cash: $${session.player.portfolio.cashBalance.toLocaleString()}
Reputation: ${session.player.reputation}/100

Traits:
- Degen Score: ${session.player.traits.degenScore}/100
- Trust Score: ${session.player.traits.trustScore}/100
- Alpha Score: ${session.player.traits.alphaScore}/100
- Wealth Score: ${session.player.traits.wealthScore}/100

${session.player.traits.degenScore >= 70 ? '(High degen - risky plays more likely to succeed)' : ''}
${session.player.traits.trustScore >= 70 ? '(High trust - NPCs will be more helpful)' : ''}
${session.player.traits.alphaScore >= 70 ? '(High alpha - better information quality)' : ''}
`

  // Format market context
  const topGainer = realMarketData.topGainers[0]
  const topGainerText = topGainer
    ? `Top gainer: ${topGainer.symbol} (+${topGainer.change24h.toFixed(1)}%)`
    : ''
  const marketContext = `
## CURRENT MARKET (for context/flavor)

SOL: $${realMarketData.solPrice}
Market Sentiment: ${realMarketData.marketSentiment}
${topGainerText}
`

  return `${OUTCOME_SYSTEM_PROMPT}

---

${situationContext}

${choiceContext}

${playerState}

${marketContext}

---

# YOUR TASK

Generate the outcome for this player's choice. Consider:
1. The risk level - ${choice.riskLevel} choices should have ${choice.riskLevel} outcomes
2. The player's traits - they affect success probability
3. The NPCs involved - their personalities matter
4. The market context - add flavor and relevance
5. Future story potential - plant seeds for callbacks

Make it dramatic. Make it memorable. Make it matter.

Output valid JSON only, no additional text.`
}

/**
 * Get appropriate effect ranges based on risk level
 * NOTE: No cash changes - portfolio only changes through player trades
 */
function getEffectRanges(
  riskLevel: GeneratedChoice['riskLevel'],
  _portfolioValue: number
): {
  repMin: number
  repMax: number
  traitMin: number
  traitMax: number
} {
  switch (riskLevel) {
    case 'safe':
      return {
        repMin: -2,
        repMax: 3,
        traitMin: -1,
        traitMax: 2,
      }
    case 'moderate':
      return {
        repMin: -8,
        repMax: 6,
        traitMin: -3,
        traitMax: 4,
      }
    case 'risky':
      return {
        repMin: -20,
        repMax: 15,
        traitMin: -6,
        traitMax: 8,
      }
    case 'yolo':
      return {
        repMin: -40,
        repMax: 30,
        traitMin: -12,
        traitMax: 12,
      }
    default:
      return {
        repMin: -3,
        repMax: 3,
        traitMin: -2,
        traitMax: 2,
      }
  }
}

/**
 * Prompt for generating follow-up situations from outcomes
 */
export function buildFollowUpPrompt(
  previousSituation: GeneratedSituation,
  outcome: GeneratedOutcome,
  _context: GameMasterContext
): string {
  return `${buildOutcomePrompt.toString()}

---

## FOLLOW-UP REQUIRED

The previous outcome triggered a follow-up situation. Generate the next beat in this story:

Previous Situation: ${previousSituation.title}
Previous Outcome Mood: ${outcome.mood}
Consequence Flags: ${outcome.consequenceFlags.join(', ')}
${outcome.foreshadowing ? `Foreshadowing: ${outcome.foreshadowing}` : ''}

This follow-up should:
1. Directly continue from the previous outcome
2. Escalate the stakes or introduce a complication
3. Give the player a new choice that matters
4. Feel like a natural story progression

The category should likely be the same or related to: ${previousSituation.category}
Mood could shift to reflect the escalation.

Output valid JSON for a GeneratedSituation, no additional text.`
}

/**
 * Degen wisdom quotes for different outcome types
 */
export const OUTCOME_QUOTES = {
  big_win: [
    "Holy shit. You actually made it work. Don't let it go to your head.",
    "Screenshot this before it reverses. You know it will.",
    "Few understand the plays you're making. You might be one of them.",
    "Congrats, you got lucky. Try not to give it all back.",
  ],
  small_win: [
    "Wow, crumbs. Don't spend it all in one place.",
    "Green is green. Barely.",
    "Not lambo money. Not even Civic money. But sure, a 'win'.",
    "The copium is strong today.",
  ],
  neutral: [
    "Nothing happened. Just like your trading career.",
    "The universe doesn't care about you specifically.",
    "Crab market energy, but for your life choices.",
    "Another day of not making it. Classic.",
  ],
  small_loss: [
    "Lmao. Anyway...",
    "It's only unrealized pain until you realize it's very real pain.",
    "Learning experience secured. Expensive tuition, though.",
    "This is fine. *everything burning*",
  ],
  big_loss: [
    "Get rekt, nerd.",
    "The market giveth, the market ruggeth. You got rugged.",
    "You've been humbled. Maybe you needed it.",
    "Rock bottom? There's always a new low to discover.",
  ],
  relationship_gained: [
    "A new 'fren' appeared. Probably wants something from you.",
    "Network effect: activated. Until they ghost you.",
    "One more person who might rug you later. Congrats.",
  ],
  relationship_lost: [
    "Burned that bridge. Hope you're a good swimmer.",
    "Making enemies is easy. You're a natural.",
    "They'll remember this. Good luck at the next conference.",
  ],
}

/**
 * Get appropriate quote for outcome type
 */
export function getOutcomeQuote(outcome: GeneratedOutcome): string {
  const { effects } = outcome
  const repChange = effects.reputationChange || 0

  let category: keyof typeof OUTCOME_QUOTES

  // Determine outcome type based on reputation change (no cash changes anymore)
  if (repChange > 15) {
    category = 'big_win'
  } else if (repChange > 5) {
    category = 'small_win'
  } else if (repChange < -15) {
    category = 'big_loss'
  } else if (repChange < -5) {
    category = 'small_loss'
  } else if (repChange > 0) {
    category = 'relationship_gained'
  } else if (repChange < 0) {
    category = 'relationship_lost'
  } else {
    category = 'neutral'
  }

  const quotes = OUTCOME_QUOTES[category]
  const index = Math.floor(Math.random() * quotes.length)
  return quotes[index] ?? quotes[0] ?? 'The dice have spoken.'
}
